import { useState } from 'react'
import Page from '../../components/layouts/page'
import DataTable from '../../components/data_table'
import RowMenu from '../../components/row_menu'
import LanguageFormModal from './language_form_modal'
import { Button, EmptyState, Text } from '@cinesend/ui-kit'
import { useQuery } from '@tanstack/react-query'
import useTableFilters from '../../hooks/use_table_filters'

const SystemLanguages = () => {
  const { data: languages, error, isLoading, refetch } = useQuery(['languages'])
  const { searching } = useTableFilters({
    enableSearch: true,
    initialSort: { key: 'eng_description', direction: 'asc' },
  })
  const [showAddModal, setShowAddModal] = useState(false)
  const [editingLanguage, setEditingLanguage] = useState(null)

  const search = (searching.search ?? '').trim().toLowerCase()
  const rows = (languages ?? [])
    .filter((language) => {
      if (!search) return true
      return [
        language.eng_description,
        language.local_description,
        language.ife_code,
        language.bcp_47_code,
        language.iso_639_2_t_code,
      ].some((value) =>
        String(value ?? '')
          .toLowerCase()
          .includes(search)
      )
    })
    .sort((a, b) =>
      (a.eng_description ?? '').localeCompare(b.eng_description ?? '', undefined, { sensitivity: 'base' })
    )

  const columns = [
    {
      key: 'eng_description',
      header: 'English name',
      render: (data) => <Text variant='sm-medium'>{data.eng_description}</Text>,
    },
    {
      key: 'local_description',
      header: 'Local name',
      render: (data) => (
        <Text variant='sm' tone='secondary'>
          {data.local_description}
        </Text>
      ),
    },
    {
      key: 'ife_code',
      header: 'IFE code',
      width: '12%',
      render: (data) => <Text variant='sm'>{data.ife_code}</Text>,
    },
    {
      key: 'bcp_47_code',
      header: 'BCP 47',
      width: '12%',
      render: (data) => <Text variant='sm'>{data.bcp_47_code || '—'}</Text>,
    },
    {
      key: 'iso_639_2_t_code',
      header: 'ISO 639-2/T',
      width: '12%',
      render: (data) => <Text variant='sm'>{data.iso_639_2_t_code}</Text>,
    },
    {
      key: 'actions',
      header: '',
      width: '50px',
      render: (data) => (
        <RowMenu
          label={`Actions for ${data.eng_description}`}
          items={[
            {
              id: 'edit',
              label: 'Edit',
              onSelect: () => setEditingLanguage(data),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <Page
      title='System Languages'
      buttons={[
        <Button key='add-language' onClick={() => setShowAddModal(true)}>
          Add language
        </Button>,
      ]}
    >
      <DataTable
        caption='System languages'
        columns={columns}
        rows={rows}
        isLoading={isLoading}
        error={error}
        onRetry={refetch}
        empty={<EmptyState title='No languages found!' />}
        searching={searching}
      />
      {showAddModal && <LanguageFormModal onClose={() => setShowAddModal(false)} />}
      {editingLanguage && <LanguageFormModal language={editingLanguage} onClose={() => setEditingLanguage(null)} />}
    </Page>
  )
}

export default SystemLanguages
